import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.tsx';
import { allowLanguages, allowSavedTenantLanguages } from './i18n/index.ts';
import { tokenStore } from './lib/api.ts';
import { loadHostBrand } from './lib/brand.ts';
import { SessionProvider } from './lib/session.tsx';
import { UiProvider } from './lib/ui-state.tsx';
import { tokensCss } from './theme/tokens.ts';
import './styles/app.css';
import './lib/offline.ts';

// Design tokens are generated from tokens.ts so the contrast test and the app share one source.
const style = document.createElement('style');
style.id = 'tokens';
style.textContent = tokensCss();
document.head.prepend(style);

// Server administration opens a business as support at /support#<token> (only inside the window
// the owner opened). The token replaces any sign-in in this browser and leaves the address bar.
if (/^\/support\/?$/.test(window.location.pathname)) {
  const token = window.location.hash.slice(1);
  if (/^[0-9a-f-]{36}\.[A-Za-z0-9_-]+$/.test(token)) tokenStore.set(token);
  history.replaceState(null, '', '/');
}

// A guest's phone (QR menu, web order, order tracking) gets only the guest pages: no sign-in,
// no till kept for offline use.
const guestPage = /^\/(m|order|track)\/[^/]+\/[^/]+\/?$/.test(window.location.pathname);
const GuestApp = lazy(() => import('./guest/GuestApp.tsx').then((m) => ({ default: m.GuestApp })));
// The business's web shop and its order pages: open to anyone, nothing of the staff's app.
const shopPage = /^\/shop\/[^/]+(\/o\/[^/]+)?\/?$/.test(window.location.pathname);
const ShopApp = lazy(() => import('./shop/ShopApp.tsx').then((m) => ({ default: m.ShopApp })));
// Where the "Connect a phone" QR code leads: getting the phone app, before anyone signs in.
const getApp = /^\/get-app\/?$/.test(window.location.pathname);
const GetApp = lazy(() => import('./guest/GetApp.tsx').then((m) => ({ default: m.GetApp })));

// "Unsubscribe" in a marketing e-mail: one button, no sign-in.
const unsubPage = /^\/unsubscribe\/[^/]+\/[0-9a-f]+\/?$/.test(window.location.pathname);
const Unsubscribe = lazy(() => import('./guest/Unsubscribe.tsx').then((m) => ({ default: m.Unsubscribe })));

// "Book a demo" in an e-mail to a prospect: they choose a time. No sign-in.
const demoPage = /^\/demo\/[^/]+\/[0-9a-f]+\/?$/.test(window.location.pathname);
const DemoBooking = lazy(() => import('./guest/DemoBooking.tsx').then((m) => ({ default: m.DemoBooking })));

// The business's website: its pages, blog and forum, open to anyone.
const sitePage = /^\/site\/[^/]+(\/[^/]+){0,2}\/?$/.test(window.location.pathname);
const SiteApp = lazy(() => import('./site/SiteApp.tsx').then((m) => ({ default: m.SiteApp })));

// The business's events: what is coming, booking, and a person's ticket. No sign-in.
const eventsPage = /^\/events\/[^/]+(\/[^/]+){0,2}\/?$/.test(window.location.pathname);
const EventsApp = lazy(() => import('./site/EventsApp.tsx').then((m) => ({ default: m.EventsApp })));

// The business's booking page for appointments, and a person's own booking. No sign-in.
const bookPage = /^\/book\/[^/]+(\/visit\/[^/]+)?\/?$/.test(window.location.pathname);
const BookApp = lazy(() => import('./site/BookApp.tsx').then((m) => ({ default: m.BookApp })));

// A survey anyone with the link answers. No sign-in.
const surveyPage = /^\/survey\/[^/]+\/[^/]+\/?$/.test(window.location.pathname);
const SurveyApp = lazy(() => import('./site/SurveyApp.tsx').then((m) => ({ default: m.SurveyApp })));

// The business's chat page, for a link or a QR code. No sign-in.
const chatPage = /^\/chat\/[^/]+\/?$/.test(window.location.pathname);
const ChatPage = lazy(() => import('./site/ChatWidget.tsx').then((m) => ({ default: m.ChatPage })));

// The business's support page: send a request, follow it, reply and rate. No sign-in.
const supportPage = /^\/support\/[^/]+(\/t\/[^/]+)?\/?$/.test(window.location.pathname);
const SupportApp = lazy(() => import('./site/SupportApp.tsx').then((m) => ({ default: m.SupportApp })));

// The business's careers page: open positions and applications, no sign-in.
const careersPage = /^\/careers\/[^/]+\/?$/.test(window.location.pathname);
const Careers = lazy(() => import('./guest/Careers.tsx').then((m) => ({ default: m.Careers })));

// A signing link and a shared file: open to whoever holds the link, no sign-in.
const signPage = /^\/sign\/[^/]+\/[^/]+\/?$/.test(window.location.pathname);
const SignPage = lazy(() => import('./guest/Sign.tsx').then((m) => ({ default: m.Sign })));
const filePage = /^\/f\/[^/]+\/[^/]+\/?$/.test(window.location.pathname);
const SharedFile = lazy(() => import('./guest/SharedFile.tsx').then((m) => ({ default: m.SharedFile })));

// A card payment link: who asks for how much, then on to the payment provider. No sign-in.
const payPage = /^\/pay\/[^/]+\/pl_[A-Za-z0-9_-]+\/?$/.test(window.location.pathname);
const PayApp = lazy(() => import('./pay/PayApp.tsx').then((m) => ({ default: m.PayApp })));

// A quote the business shared: the customer reads it, adds extras and accepts or declines. No sign-in.
const quotePage = /^\/quote\/[^/]+\/[A-Za-z0-9_-]+\/?$/.test(window.location.pathname);
const QuoteOnline = lazy(() => import('./guest/QuoteOnline.tsx').then((m) => ({ default: m.QuoteOnline })));

// Server administration (superadmin): its own sign-in and screens, nothing of a business.
const platformPage = /^\/platform\/?$/.test(window.location.pathname);
const PlatformApp = lazy(() => import('./platform/PlatformApp.tsx').then((m) => ({ default: m.PlatformApp })));

// A business's public page offers only the languages turned on for that business; the address
// names it right after the page kind (/shop/<business>, /pay/<business>/…). The staff app uses
// those of the business last signed in to here; server administration and "get the app" offer all.
const publicPage = guestPage || shopPage || sitePage || eventsPage || bookPage || surveyPage || chatPage || supportPage || careersPage || signPage || filePage || payPage || unsubPage || demoPage || quotePage;
async function languagesReady() {
  if (platformPage || getApp) return;
  if (!publicPage) return allowSavedTenantLanguages();
  const slug = window.location.pathname.split('/')[2] ?? '';
  const key = `erp.langs.${slug}`;
  try {
    const kept = sessionStorage.getItem(key);
    if (kept) return void allowLanguages(JSON.parse(kept) as string[]);
  } catch {
    // Ask the server.
  }
  try {
    const ctl = new AbortController();
    const timer = window.setTimeout(() => ctl.abort(), 2500);
    const res = await fetch(`/api/languages/${encodeURIComponent(slug)}`, { signal: ctl.signal, credentials: 'omit' });
    window.clearTimeout(timer);
    if (!res.ok) return;
    const { languages } = (await res.json()) as { languages: string[] };
    allowLanguages(languages);
    try {
      sessionStorage.setItem(key, JSON.stringify(languages));
    } catch {
      // Asked again on the next page.
    }
  } catch {
    // Offline or slow: the page opens in the device's language and the server answers in it.
  }
}

if ('serviceWorker' in navigator && import.meta.env.PROD && !guestPage && !shopPage && !sitePage && !eventsPage && !bookPage && !surveyPage && !supportPage && !chatPage && !platformPage && !unsubPage && !demoPage && !careersPage && !signPage && !filePage && !payPage && !quotePage) {
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => {}));
}

// A partner's address shows the partner's brand from the first paint (white-label).
void Promise.all([languagesReady(), platformPage ? null : loadHostBrand()]).then(() =>
  createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <UiProvider>
      {guestPage ? (
        <Suspense fallback={null}>
          <GuestApp />
        </Suspense>
      ) : shopPage ? (
        <Suspense fallback={null}>
          <ShopApp />
        </Suspense>
      ) : sitePage ? (
        <Suspense fallback={null}>
          <SiteApp />
        </Suspense>
      ) : eventsPage ? (
        <Suspense fallback={null}>
          <EventsApp />
        </Suspense>
      ) : bookPage ? (
        <Suspense fallback={null}>
          <BookApp />
        </Suspense>
      ) : surveyPage ? (
        <Suspense fallback={null}>
          <SurveyApp />
        </Suspense>
      ) : supportPage ? (
        <Suspense fallback={null}>
          <SupportApp />
        </Suspense>
      ) : chatPage ? (
        <Suspense fallback={null}>
          <ChatPage />
        </Suspense>
      ) : platformPage ? (
        <Suspense fallback={null}>
          <PlatformApp />
        </Suspense>
      ) : unsubPage ? (
        <Suspense fallback={null}>
          <Unsubscribe />
        </Suspense>
      ) : demoPage ? (
        <Suspense fallback={null}>
          <DemoBooking />
        </Suspense>
      ) : careersPage ? (
        <Suspense fallback={null}>
          <Careers />
        </Suspense>
      ) : signPage ? (
        <Suspense fallback={null}>
          <SignPage />
        </Suspense>
      ) : filePage ? (
        <Suspense fallback={null}>
          <SharedFile />
        </Suspense>
      ) : payPage ? (
        <Suspense fallback={null}>
          <PayApp />
        </Suspense>
      ) : quotePage ? (
        <Suspense fallback={null}>
          <QuoteOnline />
        </Suspense>
      ) : getApp ? (
        <Suspense fallback={null}>
          <GetApp />
        </Suspense>
      ) : (
        <SessionProvider>
          <App />
        </SessionProvider>
      )}
    </UiProvider>
  </StrictMode>,
),
);
